import React, { useState } from "react";
import PublicLayout from "./PublicLayout";
import { Phone, Mail, MapPin, MessageCircle, ArrowRight, Send, Clock } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const REQUIREMENTS = ["Hydraulic Hoses", "Fittings & Adaptors", "Custom Assembly", "Bulk / OEM Supply", "On-Site Emergency", "Technical Consultation", "Other"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", company: "", phone: "", email: "", requirement: REQUIREMENTS[0], message: "" });
  const [submitting, setSubmitting] = useState(false);
  const onChange = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async e => {
    e.preventDefault();
    if (!form.name || !form.phone) { toast.error("Please share at least your name and phone."); return; }
    setSubmitting(true);
    try {
      await api.post("/contact/inquiries", form);
      toast.success("Received. Our sales team will reach out within 24 hours.");
      setForm({ name: "", company: "", phone: "", email: "", requirement: REQUIREMENTS[0], message: "" });
    } catch { toast.error("Something went wrong. Please call or WhatsApp us instead."); }
    finally { setSubmitting(false); }
  };

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="bg-[#0A2540] text-white" data-testid="contact-hero">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-16 md:py-24">
          <div className="text-xs font-bold uppercase tracking-widest text-[#F5B301] mb-3">Contact Indus Hoses</div>
          <h1 className="font-display font-black text-4xl md:text-6xl leading-tight tracking-tight" data-testid="contact-h1">Talk to a<br /> real engineer.</h1>
          <p className="mt-5 text-white/80 text-lg max-w-2xl">No call-centres. No bots. Fill the form, or pick up the phone — you'll speak to someone who knows hoses.</p>
        </div>
      </section>

      {/* Channels strip */}
      <section className="max-w-[1400px] mx-auto px-4 lg:px-8 -mt-8 relative z-10 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="contact-channels">
        <a href="tel:+918962913616" className="p-6 bg-[#004E9B] text-white rounded-md hover:shadow-xl transition-shadow group" data-testid="channel-call">
          <Phone size={24} className="mb-3" />
          <div className="text-xs uppercase tracking-wider font-bold text-white/70">Call Sales</div>
          <div className="font-display font-black text-lg mt-1">+91 89629 13616</div>
          <div className="text-xs mt-1 text-white/80">24/7 Emergency Line</div>
        </a>
        <a href="https://wa.me/918962913616" target="_blank" rel="noopener noreferrer" className="p-6 bg-[#25D366] text-white rounded-md hover:shadow-xl transition-shadow group" data-testid="channel-whatsapp">
          <MessageCircle size={24} className="mb-3" />
          <div className="text-xs uppercase tracking-wider font-bold text-white/80">WhatsApp</div>
          <div className="font-display font-black text-lg mt-1">Send us a photo</div>
          <div className="text-xs mt-1 text-white/90">Quote in 1 hour</div>
        </a>
        <a href="mailto:sales@indushoses.co.in" className="p-6 bg-white border border-slate-200 rounded-md hover:shadow-md transition-shadow" data-testid="channel-email">
          <Mail size={24} className="mb-3 text-[#004E9B]" />
          <div className="text-xs uppercase tracking-wider font-bold text-slate-500">Email</div>
          <div className="font-display font-black text-lg mt-1 text-slate-900">sales@indushoses.co.in</div>
          <div className="text-xs mt-1 text-slate-500">Reply within 24h</div>
        </a>
        <div className="p-6 bg-white border border-slate-200 rounded-md" data-testid="channel-hours">
          <Clock size={24} className="mb-3 text-[#004E9B]" />
          <div className="text-xs uppercase tracking-wider font-bold text-slate-500">Workshop Hours</div>
          <div className="font-display font-black text-lg mt-1 text-slate-900">Mon–Sat · 9–7</div>
          <div className="text-xs mt-1 text-slate-500">Emergency line 24/7</div>
        </div>
      </section>

      {/* Form + Map */}
      <section className="max-w-[1400px] mx-auto px-4 lg:px-8 py-16 grid lg:grid-cols-12 gap-10" data-testid="contact-form-section">
        <div className="lg:col-span-7">
          <div className="text-xs font-bold uppercase tracking-widest text-[#004E9B] mb-2">Inquiry Form</div>
          <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight text-slate-900 mb-8">Send us a brief.</h2>
          <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Name *"><input value={form.name} onChange={onChange("name")} className={inputCls} placeholder="Your full name" data-testid="input-name" /></Field>
              <Field label="Company"><input value={form.company} onChange={onChange("company")} className={inputCls} placeholder="Your company (optional)" data-testid="input-company" /></Field>
              <Field label="Phone *"><input value={form.phone} onChange={onChange("phone")} className={inputCls} placeholder="+91 xxxxx xxxxx" data-testid="input-phone" /></Field>
              <Field label="Email"><input type="email" value={form.email} onChange={onChange("email")} className={inputCls} placeholder="you@company.com" data-testid="input-email" /></Field>
            </div>
            <Field label="Requirement">
              <div className="flex flex-wrap gap-2">
                {REQUIREMENTS.map(opt => (
                  <button key={opt} type="button" onClick={() => setForm(f => ({ ...f, requirement: opt }))}
                    className={`px-4 py-2 text-xs uppercase font-semibold tracking-wider border rounded-sm transition-colors ${form.requirement === opt ? "border-[#004E9B] bg-[#004E9B] text-white" : "border-slate-300 text-slate-600 hover:border-[#004E9B]"}`}
                    data-testid={`req-${opt.replace(/\W/g, "").toLowerCase()}`}>{opt}</button>
                ))}
              </div>
            </Field>
            <Field label="Details"><textarea rows={5} value={form.message} onChange={onChange("message")} className={`${inputCls} resize-none`} placeholder="Tell us about your application — hose specs, quantities, timeline, or attach a drawing later." data-testid="input-message" /></Field>
            <button type="submit" disabled={submitting} className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#004E9B] hover:bg-[#003268] disabled:opacity-60 text-white font-bold text-sm uppercase tracking-wider rounded-sm transition-colors" data-testid="submit-inquiry">
              {submitting ? "Sending…" : <>Submit Inquiry <Send size={16} /></>}
            </button>
          </form>
        </div>
        <div className="lg:col-span-5">
          <div className="text-xs font-bold uppercase tracking-widest text-[#004E9B] mb-2">Visit</div>
          <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight text-slate-900 mb-6">Prayagraj HQ</h2>
          <div className="border border-slate-200 rounded-md overflow-hidden aspect-[4/3]" data-testid="contact-map">
            <iframe title="Indus Hoses Prayagraj" src="https://www.google.com/maps?q=Prayagraj,+Uttar+Pradesh,+India&output=embed" className="w-full h-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </div>
          <div className="mt-5 space-y-3 text-sm text-slate-700">
            <div className="flex items-start gap-3"><MapPin size={18} className="text-[#004E9B] mt-0.5 shrink-0" /><span>Indus Hoses Pvt. Ltd., Prayagraj, Uttar Pradesh, India</span></div>
            <div className="flex items-start gap-3"><Phone size={18} className="text-[#004E9B] mt-0.5 shrink-0" /><a href="tel:+918962913616" className="hover:text-[#004E9B]">+91 89629 13616</a></div>
            <div className="flex items-start gap-3"><Mail size={18} className="text-[#004E9B] mt-0.5 shrink-0" /><a href="mailto:sales@indushoses.co.in" className="hover:text-[#004E9B]">sales@indushoses.co.in</a></div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

const inputCls = "w-full px-4 py-3 bg-white border border-slate-300 focus:border-[#004E9B] focus:ring-2 focus:ring-[#004E9B]/20 focus:outline-none text-slate-900 text-sm rounded-sm transition-colors";

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">{label}</div>
      {children}
    </label>
  );
}
